<template>
    <div class="px-1">
        <modal
            :visible="creationMode"
            :width="dialogWidth"
            @update:visible="creationMode = $event"
        >
            <div class="pb-4">
                <RedesignHtmlEditor
                    v-if="creationMode"
                    @on-update="addNote"
                />
            </div>
        </modal>
        <div
            v-for="(note, index) in fetchNotes"
            :key="index"
            class="flex flex-row justify-between"
        >
            <div class="border p-2 w-48 text-center">
                {{ getFormattedDate(note.date) }}
            </div>
            <div class="border p-2 flex-1">
                <div class="text-main-sm">
                    <RedesignHtmlEditor
                        v-if="noteToUpdate === note.date"
                        :value="note.text"
                        @on-update="handleNoteUpdate($event, note)"
                    />
                    <div
                        v-else
                        v-html="note.text"
                    />
                </div>
            </div>
            <div
                v-if="showActions(note)"
                class="border p-2 w-32"
            >
                <div class="flex justify-center">
                    <el-tooltip
                        :content="$t('widget.editor.displayNoteOnTheList')"
                        :open-delay="openDelay"
                        class="item"
                        effect="dark"
                        placement="top"
                    >
                        <eye-off-icon
                            v-if="!note.displayed"
                            class="text-primary w-4 cursor-pointer"
                            @click="displayNoteInList(true, note)"
                        />
                    </el-tooltip>
                    <el-tooltip
                        :content="$t('widget.editor.hideNoteFromList')"
                        :open-delay="openDelay"
                        class="item"
                        effect="dark"
                        placement="top"
                    >
                        <eye-icon
                            v-if="note.displayed"
                            class="text-primary w-4 cursor-pointer"
                            @click="displayNoteInList(false, note)"
                        />
                    </el-tooltip>
                    <el-tooltip
                        :content="$t('widget.editor.openEditModeForThisNote')"
                        :open-delay="openDelay"
                        class="item"
                        effect="dark"
                        placement="top"
                    >
                        <edit-3-icon
                            class="text-green w-4 mx-2 cursor-pointer"
                            cursor-pointer
                            @click="onEditNote(note)"
                        />
                    </el-tooltip>
                    <el-tooltip
                        :content="$t('widget.editor.removeNote')"
                        :open-delay="openDelay"
                        class="item"
                        effect="dark"
                        placement="top"
                    >
                        <trash-2-icon
                            class="text-red w-4 cursor-pointer"
                            @click="removeNote(note)"
                        />
                    </el-tooltip>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import get from 'lodash/get'
import { MessageBox } from 'element-ui'
import { Edit3Icon, EyeIcon, EyeOffIcon, Trash2Icon } from 'vue-feather-icons'
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { set } from 'vue'
import i18n from '@/i18n'
import { Note, WidgetExtended } from '@/types/dashboard'
import { dayjs } from '@/composables/useDayjs'

const RedesignHtmlEditor = defineAsyncComponent(() => import('@/components/RedesignBaseComponents/RedesignHtmlEditor.vue'))
const Modal = defineAsyncComponent(() => import('@/components/Common/Modal.vue'))

const props = withDefaults(
    defineProps<{
        data: WidgetExtended
        editable?: boolean
        onEditMode?: boolean
    }>(),
    {
        editable: false,
        onEditMode: false,
    }
)

const emit = defineEmits<{ (e: 'on-update', data: WidgetExtended): void }>()

const creationMode = ref(false)
const editMode = ref(false)
const noteToUpdate = ref<number | null>(null)
const openDelay = 400
const dialogWidth = '90%'

const fetchNotes = computed(() => {
    const allNotes = get(props.data?.WidgetLayout, 'Notes', []) ?? []
    if (editMode.value) {
        return allNotes
    }
    return allNotes.filter((note) => note.displayed)
})

function getFormattedDate (date: number) {
    return dayjs(date).format('YY-MM-DD HH:mm')
}

function onAddNote () {
    creationMode.value = !creationMode.value
}

function onEditNote (note: Note) {
    noteToUpdate.value = note.date
}

function newNoteObject (): Note {
    return {
        date: new Date().getTime(),
        text: '',
        displayed: true,
    }
}

function getNoteIndex (note: Note) {
    return (props.data?.WidgetLayout?.Notes ?? []).findIndex((el) => el.date === note.date)
}

function emmitUpdate () {
    if (props.data?.WidgetLayout) {
        props.data.WidgetLayout.editMode = editMode.value
    }
    emit('on-update', props.data)
    noteToUpdate.value = null
}

function addNote (val: string) {
    creationMode.value = false
    if (val && props.data?.WidgetLayout) {
        const note = newNoteObject()
        note.text = val
        if (!props.data.WidgetLayout.Notes) {
            props.data.WidgetLayout.Notes = []
        }
        props.data.WidgetLayout.Notes.push(note)
        emmitUpdate()
    }
}

function removeNote (note: Note) {
    MessageBox.confirm(
        String(i18n.t('common.confirm.question', { action: i18n.t('widget.editor.toRemoveThisNote') })),
        String(i18n.t('widget.editor.removeNote')),
        {
            cancelButtonText: String(i18n.t('common.cancel')),
            confirmButtonText: String(i18n.t('common.confirm')),
        }
    ).then(() => {
        if (props.data?.WidgetLayout?.Notes) {
            const noteIndex = getNoteIndex(note)
            props.data.WidgetLayout.Notes.splice(noteIndex, 1)
        }
        emmitUpdate()
    })
}

function displayNoteInList (state: boolean, note: Note) {
    note.displayed = state
    fetchAndUpdate(note)
}

function handleNoteUpdate (data: string, note: Note) {
    updateNote(data, note)
}

function updateNote (text: string, note: Note) {
    note.text = text
    fetchAndUpdate(note)
}

function fetchAndUpdate (note: Note) {
    if (props.data?.WidgetLayout?.Notes) {
        const noteIndex = getNoteIndex(note)
        props.data.WidgetLayout.Notes[noteIndex] = note
    }
    emmitUpdate()
}

function showActions (note: Note) {
    return editMode.value && (noteToUpdate.value == null || noteToUpdate.value !== note.date)
}

onMounted(() => {
    if (props.data?.WidgetLayout && !('Notes' in props.data.WidgetLayout)) {
        set(props.data.WidgetLayout, 'Notes', [])
    }
})

watch(
    () => props.onEditMode, 
    (state) => {
        editMode.value = state ?? false
    }, 
    {
        immediate: true 
    }
)

defineExpose({
    onAddNote
})
</script>
